/**
 * Content-schema versioning (architecture.md §5).
 *
 * @remarks
 * Every top-level authored artifact carries a `schemaVersion`. Pre-1.0 the
 * policy is EXACT match: a pack authored against any other schema version is
 * rejected at validation time with a message naming the supported version.
 * This schema stays the truth of the PRESENT; the passage from the past is
 * the loader's (R13: `content-loader/pack-migrations.ts` walks older
 * documents forward BEFORE validation, so this gate only ever refuses what
 * no registered migration reaches).
 *
 * **0.2.0 (D156)** is the first version this project has ever had a second
 * of. Every change before it was additive, so exact-match cost nothing and
 * the migration registry shipped empty on purpose, waiting for "the first
 * real migration as data with before/after fixtures". `exitLock` losing
 * `lockedFlag`, `keyItemId`, `lockedNarratives` and
 * `lockedWithKeyItemNarratives` is that migration.
 *
 * It has a consequence worth stating where the number lives: a save pins
 * `bundleVersion` to the pack's `schemaVersion` (R10), so bumping this
 * refuses every save taken against 0.1.0 — even though `RuntimeState` itself
 * is untouched by D156. Mike ruled that pre-1.0, on a demo, the pin doing
 * exactly what it was written to do beats weakening it to save one bump.
 *
 * **0.3.0 (D159)** retires `openable.openDescription` / `closedDescription`:
 * sugar over the conditional description that desugared in the ENGINE, so
 * three readers besides the engine knew the second spelling — D158's rule
 * that sugar desugars at one seam ahead of every reader, failed. The
 * migration folds the pair into the variants it always meant, byte for byte.
 *
 * **0.4.0 (D192)** makes the narrator content: `source` is one of the
 * engine's four fixed kinds or a speaker the pack declares in `speakers`,
 * where it had been an enum that named the POC's character. The migration
 * declares whichever speaker ids a document's lines already use — for
 * `eurydice`, the declaration the POC carries — and touches no line.
 *
 * **0.5.0 (D215)** takes the hologram out of the builder's hands (Mike,
 * 2026-09-27): `presentation.map.style` and `.styles` name `basic` or
 * `scene`, and the hologram is only what `scene` shows where the browser
 * cannot draw it. The migration reads `holo` as `scene` wherever a 0.4.0
 * pack chose it, once, in place.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

/** The current content-schema version, stamped on every authored artifact. */
export const CONTENT_SCHEMA_VERSION = '0.5.0';

/**
 * The exact-match `schemaVersion` gate (pre-1.0 policy). Rejections name the
 * one supported version so pack authors know exactly what to target.
 */
export const contentSchemaVersionSchema = z.literal(
	CONTENT_SCHEMA_VERSION,
	`Unsupported schemaVersion: this build of @orpheus/content-schema accepts exactly "${CONTENT_SCHEMA_VERSION}" (pre-1.0 exact-match policy)`,
);
